"use client";

import { useReplay } from "@/contexts/replay-context";

/**
 * Ce qu'un replay a besoin de dire, et rien d'autre.
 *
 * # Les commandes sont celles du studio
 *
 * Lecture, pause et retour au début sont dans la barre d'outils, là où elles
 * sont pour un morceau (voir `toolbar.tsx`) : un lecteur de plus au bas de
 * l'écran aurait fait deux transports à l'écran, et deux endroits où chercher
 * le même geste. Se déplacer se fait au rouleau — molette, glissé, minimap —,
 * et le lecteur suit la vue.
 *
 * # Restent deux moments
 *
 * Le temps que les journaux arrivent, et le cas où ils n'arrivent pas : sans un
 * mot, un studio vide ne dirait pas s'il charge, s'il a échoué, ou si le récital
 * était silencieux.
 */
export function ReplayNotice() {
  const replay = useReplay();

  if (replay.recitalId === null || replay.status === "ready") {
    return null;
  }

  // Un récital enregistré sans un geste : l'enregistreur a bien déposé, mais il
  // n'y a pas de morceau à charger. Sans un mot, le rouleau vide passerait pour
  // une panne.
  if (replay.status === "empty") {
    return (
      <div className={SHELL}>
        <span className="text-[13px] text-text-secondary">
          Ce récital n&apos;a rien à rejouer : aucune note n&apos;a été enregistrée.
        </span>
      </div>
    );
  }

  if (replay.status === "error") {
    return (
      <div className={SHELL} role="alert">
        <span className="text-[13px] text-err">
          {replay.error ?? "Ce replay n'a pas pu être ouvert."}
        </span>
      </div>
    );
  }

  return (
    <div className={SHELL}>
      <span className="text-[13px] text-text-secondary">Ouverture du replay…</span>
    </div>
  );
}

const SHELL =
  "pointer-events-none fixed bottom-4 left-1/2 z-30 flex max-w-[calc(100vw-2rem)] -translate-x-1/2 items-center gap-3 rounded-xl border border-border-subtle bg-bg-elevated/95 px-3.5 py-2 shadow-lg backdrop-blur";
